// POST /api/rules/draft, GET /api/rules/draft/{id}, POST
// /api/rules/draft/{id}/commit, DELETE /api/rules/draft/{id} -- the "Tell me
// a rule" flow, design §6.2 / execution plan §7.2, §9.2.
//
// The parse step never writes anything by itself -- `POST .../draft` returns
// a preview (restatement, resolved fields, match count, up to 20 samples)
// that the user must explicitly Commit. Resolution is strict membership
// against the caller's own vocabulary: an unresolvable phrase is a real
// `400 unresolved_label` naming the exact failing phrase (never fuzzy-matched
// into someone's ledger -- design §6.2), surfaced here as
// `UnresolvedLabelError` so the UI can render the phrase and a rephrase
// affordance instead of a generic failure. `409 draft_expired` (lazy expiry)
// is surfaced as `DraftExpiredError` so the UI can offer a re-run action.
import axios from 'axios';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import client from '../client';
import { isErrorEnvelope, parseEnvelope } from '../envelope';

export const ruleDraftKindSchema = z.enum(['event', 'merchant', 'keyword', 'account']);
export type RuleDraftKind = z.infer<typeof ruleDraftKindSchema>;

// Matches `RuleDraftResolvedOut` (backend/schemas/rules.py) exactly. The
// backend resolves each label phrase to a real row id during `POST
// /api/rules/draft` (`backend/services/rules/draft_resolve.py`) and returns
// the id, never the original label string -- the previous `*_label: string`
// fields here didn't exist on the wire at all, so every real draft response
// failed to parse (same class of bug as ENG-R1-003).
export const ruleDraftResolvedSchema = z.object({
  kind: ruleDraftKindSchema,
  name: z.string(),
  start_date: z.string().nullable(),
  end_date: z.string().nullable(),
  location: z.string().nullable(),
  merchant_id: z.string().nullable(),
  keyword: z.string().nullable(),
  account_id: z.string().nullable(),
  scope: z.string().nullable(),
  category_id: z.string().nullable(),
  // Additive (ENG-R1-007): server-resolved merchant display name, so the
  // Merchant field never depends on a sample transaction being loaded.
  // Optional on the wire for forward/backward compatibility with older
  // responses that predate this field.
  merchant_display: z.string().nullable().optional(),
});
export type RuleDraftResolved = z.infer<typeof ruleDraftResolvedSchema>;

// Matches `RuleDraftSample` (backend/schemas/rules.py) exactly:
// `line_item_id`, `transaction_id`, `amount` only -- the backend never
// sends `posted_at`/`merchant_display` on this shape (see
// `backend/routes/rules.py::_samples_for`).
export const ruleDraftSampleSchema = z.object({
  line_item_id: z.string(),
  transaction_id: z.string(),
  amount: z.string(), // signed decimal string, per money.ts contract
});
export type RuleDraftSample = z.infer<typeof ruleDraftSampleSchema>;

export const ruleDraftSchema = z.object({
  draft_id: z.string(),
  restatement: z.string(),
  resolved: ruleDraftResolvedSchema,
  preview_count: z.number(),
  samples: z.array(ruleDraftSampleSchema),
  expires_at: z.string(),
});
export type RuleDraft = z.infer<typeof ruleDraftSchema>;

export const ruleDraftCommitResultSchema = z.object({
  event_id: z.string().nullable(),
  rule_id: z.string().nullable(),
  job_id: z.string(),
  batch_id: z.string(),
});
export type RuleDraftCommitResult = z.infer<typeof ruleDraftCommitResultSchema>;

export const ruleDraftDiscardResultSchema = z.object({ discarded: z.literal(true) });

/** `400 unresolved_label` -- carries the exact failing phrase, never a fuzzy guess. */
export class UnresolvedLabelError extends Error {
  readonly phrase: string;
  constructor(message: string, phrase: string) {
    super(message);
    this.name = 'UnresolvedLabelError';
    this.phrase = phrase;
  }
}

/** `409 draft_expired` -- the caller renders a re-run action, not a silent retry. */
export class DraftExpiredError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'DraftExpiredError';
  }
}

/** `409 changed_since` on commit -- matching transactions changed since this preview (§7 Commit conflict). */
export class CommitConflictError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'CommitConflictError';
  }
}

/** No HTTP response reached the caller (timeout / network failure) -- the write's outcome is genuinely unknown (§7). */
export class RuleDraftUnknownOutcomeError extends Error {
  constructor() {
    super('We could not confirm whether the request completed.');
    this.name = 'RuleDraftUnknownOutcomeError';
  }
}

function rethrowKnownRuleDraftError(error: unknown): never {
  if (axios.isAxiosError(error)) {
    if (error.response && isErrorEnvelope(error.response.data)) {
      const { code, message, details } = error.response.data.error;
      if (code === 'unresolved_label') {
        const phrase =
          details && typeof details === 'object' && 'phrase' in details && typeof (details as { phrase: unknown }).phrase === 'string'
            ? (details as { phrase: string }).phrase
            : message;
        throw new UnresolvedLabelError(message, phrase);
      }
      if (code === 'draft_expired') {
        throw new DraftExpiredError(message);
      }
      if (code === 'changed_since') {
        throw new CommitConflictError(message);
      }
    }
    if (!error.response) {
      // Request sent, no response received -- never treated as a definitive rejection.
      throw new RuleDraftUnknownOutcomeError();
    }
  }
  throw error;
}

/** `POST /api/rules/draft {sentence}` -- parse + preview, writes nothing. */
export function useCreateRuleDraft() {
  return useMutation<RuleDraft, Error, { sentence: string }>({
    mutationFn: async ({ sentence }) => {
      try {
        const response = await client.post('/api/rules/draft', { sentence });
        return parseEnvelope(ruleDraftSchema, response.data).data;
      } catch (error) {
        rethrowKnownRuleDraftError(error);
      }
    },
  });
}

/** `GET /api/rules/draft/{id}` -- re-read a draft (e.g. after a re-run). */
export function useRuleDraft(draftId: string | null | undefined) {
  return useQuery<RuleDraft>({
    queryKey: ['rules', 'draft', draftId],
    enabled: Boolean(draftId),
    queryFn: async () => {
      try {
        const response = await client.get(`/api/rules/draft/${draftId}`);
        return parseEnvelope(ruleDraftSchema, response.data).data;
      } catch (error) {
        rethrowKnownRuleDraftError(error);
      }
    },
  });
}

/** `POST /api/rules/draft/{id}/commit {hard}` -- writes events/rules under one batch_id. */
export function useCommitRuleDraft() {
  const queryClient = useQueryClient();
  return useMutation<RuleDraftCommitResult, Error, { draftId: string; hard: boolean }>({
    mutationFn: async ({ draftId, hard }) => {
      try {
        const response = await client.post(`/api/rules/draft/${draftId}/commit`, { hard });
        return parseEnvelope(ruleDraftCommitResultSchema, response.data).data;
      } catch (error) {
        rethrowKnownRuleDraftError(error);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rules'] });
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
    },
  });
}

/** `DELETE /api/rules/draft/{id}` -- discard, status='discarded'. */
export function useDiscardRuleDraft() {
  return useMutation<{ discarded: true }, Error, string>({
    mutationFn: async (draftId) => {
      const response = await client.delete(`/api/rules/draft/${draftId}`);
      return parseEnvelope(ruleDraftDiscardResultSchema, response.data).data;
    },
  });
}
